import React, { ReactElement, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { types as api } from '@mesa-labs/mesa-api';

import { useSelector } from '../../../redux/hooks';
import useProgressBarSteps from '../../../hooks/useProgressBarSteps';
import useOnboardingCollectionScheme from '../../../hooks/useOnboardingCollectionScheme';

const ProgressBarContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
`;

const StepsMobile = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;

  @media (min-width: ${(props) => props.theme.breakpoints.Laptop}) {
    display: none;
  }
`;

const StepsDesktop = styled.div`
  align-items: center;
  display: none;
  gap: 0px;
  justify-content: space-between;

  @media (min-width: ${(props) => props.theme.breakpoints.Laptop}) {
    display: flex;
  }
`;

const Step = styled.div`
  align-items: center;
  border-radius: 4px;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-height: 36px;
  padding: 8px;
`;

const StepNumber = styled(motion.div)`
  align-items: center;
  border-radius: 999px;
  border: 1px solid transparent;
  box-sizing: border-box;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  height: 24px;
  justify-content: center;
  line-height: 1.5;
  padding: 2px;
  position: relative;
  width: 24px;
`;

const StepName = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  transition: color 300ms ease-in-out;
  white-space: nowrap;
`;

const StepArrow = styled.img`
  opacity: 0.5;
`;

const CompletedCheckIcon = styled(motion.img)`
  height: 15px;
  width: 15px;
`;

const MobileProgressPill = styled(motion.div)`
  border-radius: 999px;
  height: 4px;
  width: 40px;
`;

const MobileProgressPills = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const numberTransition = { duration: 0.15 };

const checkTransition = {
  delay: 0.3,
  duration: 0.6,
  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
};

function ProgressBar(): ReactElement {
  const location = useLocation();

  const customMsa = useSelector((state) => state.onboarding.customMsa);
  const availablePrograms = useSelector((state) => state.onboarding.availablePrograms);
  const referralCode = useSelector((state) => state.vendor.vendor.referralCode);
  const demoModeCode = useSelector((state) => state.demoMode.demoModeCode);

  const { isDirectDebit: directDebit } = useOnboardingCollectionScheme();

  const hasOnDemandProgramOverride = (availablePrograms || []).some((override) => override.programCode === api.ProgramCodes.OD);

  const onDemand = useMemo(() => hasOnDemandProgramOverride || referralCode === api.ProgramCodes.OD || api.ProgramCodes.OD === demoModeCode,
    [hasOnDemandProgramOverride, referralCode, demoModeCode]
  );

  const stepContent = useProgressBarSteps({
    onDemand,
    customMsa,
    directDebit,
  });

  const currentStep = useMemo(
    () => {
      const current = stepContent.find(
        (step) => step.routes.some((route) => location.pathname.includes(route)),
      );

      if (!current) {
        return -1;
      }

      return current.step;
    },
    [location, stepContent],
  );

  const getColorsForStep = (step: number) => {
    if (currentStep === step) {
      return {
        name: '#4700D1',
        borderColor: '#4700D1',
        background: '#4700D1',
        number: '#FFFFFF',
      };
    } else if (step > currentStep) {
      return {
        name: '#40396C',
        borderColor: '#E1E4EA',
        background: 'rgba(255, 255, 255, 0)',
        number: '#40396C',
      };
    }

    return {
      name: '#40396C',
      borderColor: '#ECEEF3',
      background: '#ECEEF3',
      number: '#40396C',
    };
  };

  const numVisibleSteps = stepContent.filter((step) => step.visible).length;

  return (
    <ProgressBarContainer>
      <StepsMobile>
        <MobileProgressPills>
          {
            stepContent.map((step) => {
              if (!step.visible) {
                return null;
              }

              return (
                <MobileProgressPill
                  key={step.name}
                  animate={{ backgroundColor: currentStep >= step.step ? '#5A05FF' : '#EEE5FF' }}
                  initial={false}
                  transition={{ duration: 0.3 }}
                />
              );
            })
          }
        </MobileProgressPills>
      </StepsMobile>

      <StepsDesktop>
        {
          stepContent.map((step, idx) => {
            if (!step.visible) {
              return null;
            }

            const colors = getColorsForStep(step.step);
            const isActive = currentStep === step.step;
            const isComplete = currentStep > step.step;

            return (
              <React.Fragment key={step.name}>
                <Step>
                  <StepNumber
                    animate={{
                      backgroundColor: colors.background,
                      borderColor: colors.borderColor,
                      color: colors.number,
                      scale: isActive ? [0.9, 1] : 1,
                    }}
                    initial={false}
                    transition={{
                      default: { duration: 0.3, ease: 'easeOut' },
                      scale: { type: 'spring', stiffness: 400, damping: 25 },
                    }}
                  >
                    <AnimatePresence initial={false} mode="popLayout">
                      {!isComplete && (
                        <motion.span
                          key="number"
                          animate={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                          exit={{ filter: 'blur(2px)', opacity: 0, scale: 0.8 }}
                          initial={{ filter: 'blur(2px)', opacity: 0, scale: 0.8 }}
                          transition={numberTransition}
                        >
                          {idx + 1}
                        </motion.span>
                      )}

                      {isComplete && (
                        <CompletedCheckIcon
                          key="check"
                          animate={{ clipPath: 'inset(0 0% 0 0)', filter: 'blur(0px)', opacity: 1 }}
                          exit={{ opacity: 0, transition: numberTransition }}
                          initial={{ clipPath: 'inset(0 100% 0 0)', filter: 'blur(2px)', opacity: 0 }}
                          alt=""
                          src="/assets/progress-check-icon.svg"
                          transition={checkTransition}
                        />
                      )}
                    </AnimatePresence>
                  </StepNumber>

                  <StepName color={colors.name}>{step.name}</StepName>
                </Step>

                {idx !== numVisibleSteps - 1 && <StepArrow src="/assets/arrow-right.svg" alt="" />}
              </React.Fragment>
            );
          })
        }
      </StepsDesktop>
    </ProgressBarContainer>
  );
}

export default ProgressBar;
